import { __decorate } from "tslib";
import { ArrayDecoder, AutoEncoder, BooleanDecoder, DateDecoder, EnumDecoder, field, IntegerDecoder, MapDecoder, NumberDecoder, StringDecoder } from "@simonbackx/simple-encoding";
import { v4 as uuidv4 } from "uuid";
import { RecordAnswerDecoder } from "./members/records/RecordAnswer";
import { RecordCategory } from "./members/records/RecordCategory";
export var DocumentStatus;
(function (DocumentStatus) {
    DocumentStatus["Draft"] = "Draft";
    DocumentStatus["MissingData"] = "MissingData";
    DocumentStatus["Published"] = "Published";
    DocumentStatus["Deleted"] = "Deleted";
})(DocumentStatus || (DocumentStatus = {}));
export class DocumentStatusHelper {
    static getName(status) {
        switch (status) {
            case DocumentStatus.Draft: return "Klad";
            case DocumentStatus.MissingData: return "Onvolledig";
            case DocumentStatus.Published: return "Gepubliceerd";
            case DocumentStatus.Deleted: return "Verwijderd";
        }
    }
    static getColor(status) {
        switch (status) {
            case DocumentStatus.Draft: return "info";
            case DocumentStatus.MissingData: return "tertiary";
            case DocumentStatus.Published: return "secundary";
            case DocumentStatus.Deleted: return "error";
        }
    }
}
export class DocumentSettings extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.name = "";
        this.maxAge = null;
        this.minPrice = null;
        /**
         * Fields defined by the template that can be set.
         */
        this.fieldAnswers = [];
        /**
         * Defines where to automatically find the answer for a given question
         */
        this.linkedFields = new Map();
    }
}
__decorate([
    field({ decoder: StringDecoder, version: 178 })
], DocumentSettings.prototype, "name", void 0);
__decorate([
    field({ decoder: IntegerDecoder, nullable: true })
], DocumentSettings.prototype, "maxAge", void 0);
__decorate([
    field({ decoder: IntegerDecoder, nullable: true })
], DocumentSettings.prototype, "minPrice", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(RecordAnswerDecoder) })
], DocumentSettings.prototype, "fieldAnswers", void 0);
__decorate([
    field({ decoder: new MapDecoder(StringDecoder, new ArrayDecoder(StringDecoder)) })
], DocumentSettings.prototype, "linkedFields", void 0);
export class DocumentTemplateDefinition extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.type = null;
        this.name = "";
        this.fieldCategories = [];
        /**
         * Questions filled out for each selected group / cycle combination
         */
        this.groupFieldCategories = [];
        /**
         * Questions that are different for each document. They can be linked to specific fields of members later on for automatic linking.
         */
        this.documentFieldCategories = [];
        this.defaultMaxAge = null;
        this.defaultMinPrice = null;
        this.exportFieldCategories = [];
        this.xmlExport = null;
        this.xmlExportDescription = null;
    }
}
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 194, upgrade: () => 'fiscal' })
], DocumentTemplateDefinition.prototype, "type", void 0);
__decorate([
    field({ decoder: StringDecoder })
], DocumentTemplateDefinition.prototype, "name", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(RecordCategory) })
], DocumentTemplateDefinition.prototype, "fieldCategories", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(RecordCategory) })
], DocumentTemplateDefinition.prototype, "groupFieldCategories", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(RecordCategory) })
], DocumentTemplateDefinition.prototype, "documentFieldCategories", void 0);
__decorate([
    field({ decoder: IntegerDecoder, nullable: true })
], DocumentTemplateDefinition.prototype, "defaultMaxAge", void 0);
__decorate([
    field({ decoder: IntegerDecoder, nullable: true })
], DocumentTemplateDefinition.prototype, "defaultMinPrice", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(RecordCategory), version: 179 })
], DocumentTemplateDefinition.prototype, "exportFieldCategories", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 179 })
], DocumentTemplateDefinition.prototype, "xmlExport", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 179 })
], DocumentTemplateDefinition.prototype, "xmlExportDescription", void 0);
export class DocumentTemplateGroup extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.groupId = "";
        this.cycle = 0;
        /**
         * Answers for groupFieldCategories for this group
         */
        this.fieldAnswers = [];
    }
}
__decorate([
    field({ decoder: StringDecoder })
], DocumentTemplateGroup.prototype, "groupId", void 0);
__decorate([
    field({ decoder: NumberDecoder })
], DocumentTemplateGroup.prototype, "cycle", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(RecordAnswerDecoder) })
], DocumentTemplateGroup.prototype, "fieldAnswers", void 0);
export class DocumentPrivateSettings extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.templateDefinition = DocumentTemplateDefinition.create({});
        /**
         * Groups for which the members will receive the document
         */
        this.groups = [];
    }
}
__decorate([
    field({ decoder: DocumentTemplateDefinition })
], DocumentPrivateSettings.prototype, "templateDefinition", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(DocumentTemplateGroup) })
], DocumentPrivateSettings.prototype, "groups", void 0);
export class DocumentTemplatePrivate extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.status = DocumentStatus.Draft;
        this.updatesEnabled = true;
        this.html = "";
        this.settings = DocumentSettings.create({});
        this.privateSettings = DocumentPrivateSettings.create({});
        this.createdAt = new Date();
        this.updatedAt = new Date();
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], DocumentTemplatePrivate.prototype, "id", void 0);
__decorate([
    field({ decoder: new EnumDecoder(DocumentStatus) })
], DocumentTemplatePrivate.prototype, "status", void 0);
__decorate([
    field({ decoder: BooleanDecoder })
], DocumentTemplatePrivate.prototype, "updatesEnabled", void 0);
__decorate([
    field({ decoder: StringDecoder })
], DocumentTemplatePrivate.prototype, "html", void 0);
__decorate([
    field({ decoder: DocumentSettings })
], DocumentTemplatePrivate.prototype, "settings", void 0);
__decorate([
    field({ decoder: DocumentPrivateSettings })
], DocumentTemplatePrivate.prototype, "privateSettings", void 0);
__decorate([
    field({ decoder: DateDecoder })
], DocumentTemplatePrivate.prototype, "createdAt", void 0);
__decorate([
    field({ decoder: DateDecoder })
], DocumentTemplatePrivate.prototype, "updatedAt", void 0);
/**
 * Data stored with a document
 */
export class DocumentData extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.name = "";
        this.description = "";
        /**
         * Contains a snapshot of all the answers
         */
        this.fieldAnswers = [];
    }
    matchQuery(query) {
        return !!this.fieldAnswers.find(a => a.matchQuery(query));
    }
}
__decorate([
    field({ decoder: StringDecoder })
], DocumentData.prototype, "name", void 0);
__decorate([
    field({ decoder: StringDecoder })
], DocumentData.prototype, "description", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(RecordAnswerDecoder) })
], DocumentData.prototype, "fieldAnswers", void 0);
export class Document extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.number = null;
        this.status = DocumentStatus.Draft;
        this.data = DocumentData.create({});
        this.createdAt = new Date();
        this.updatedAt = new Date();
        this.memberId = null;
        this.registrationId = null;
    }
    matchQuery(query) {
        return this.data.matchQuery(query);
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], Document.prototype, "id", void 0);
__decorate([
    field({ decoder: NumberDecoder, nullable: true, version: 179 })
], Document.prototype, "number", void 0);
__decorate([
    field({ decoder: new EnumDecoder(DocumentStatus) })
], Document.prototype, "status", void 0);
__decorate([
    field({ decoder: DocumentData })
], Document.prototype, "data", void 0);
__decorate([
    field({ decoder: DateDecoder })
], Document.prototype, "createdAt", void 0);
__decorate([
    field({ decoder: DateDecoder })
], Document.prototype, "updatedAt", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true })
], Document.prototype, "memberId", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true })
], Document.prototype, "registrationId", void 0);
__decorate([
    field({ decoder: StringDecoder })
], Document.prototype, "templateId", void 0);
//# sourceMappingURL=Document.js.map